export enum BillingTier {
  FREE = 'free',
  STARTER = 'starter',
  GROWTH = 'growth',
}

export type ProductLine = 'comply' | 'quality' | 'full';

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
}

export interface TierInfo {
  tier: BillingTier;
  name: string;
  price_monthly: string;
  minutes_included: number;
  overage_rate: string;
  features: string[];
  allows_analysis: boolean;
}

export interface ProductTierInfo extends TierInfo {
  product_line: ProductLine;
}

export interface ProductTiersResponse {
  product_line: ProductLine;
  tiers: ProductTierInfo[];
}

export interface SubscriptionResponse {
  id: string;
  user_id: string;
  tier: BillingTier;
  status: SubscriptionStatus;
  pending_tier: BillingTier | null;  // Tier to change to at period end (for scheduled downgrades)
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  minutes_included: number;
  minutes_used: number;
  overage_minutes: number;
  minutes_remaining: number;
  current_period_start: string | null;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
  canceled_at: string | null;
  tier_info: TierInfo;
}

export interface UsageSummary {
  current_period_start: string | null;
  current_period_end: string | null;
  minutes_included: number;
  minutes_used: number;
  minutes_used_in_plan: number;
  minutes_remaining: number;
  overage_minutes: number;
  total_analyses: number;
  transcription_only_count: number;
  analysis_count: number;
}

export interface BillingSummary {
  subscription: SubscriptionResponse;
  usage: UsageSummary;
  estimated_overage_cost: string;
}

export interface CheckoutSessionCreate {
  tier: BillingTier;
  success_url: string;
  cancel_url: string;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface PortalSessionResponse {
  portal_url: string;
}

export interface TiersResponse {
  tiers: TierInfo[];
}

export interface SubscriptionActionResponse {
  message: string;
  subscription_id: string;
}

export interface InvoiceLine {
  description: string;
  amount: number;
  quantity: number;
  period_start: number | null;
  period_end: number | null;
}

export interface Invoice {
  id: string;
  invoice_number: string | null;
  status: string;
  amount_due: number;
  amount_paid: number;
  subtotal: number;
  total: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  description: string;
  lines: InvoiceLine[];
}

export interface InvoiceHistoryResponse {
  invoices: Invoice[];
  has_more: boolean;
}

// Stripe pricing from API
export interface StripePrice {
  id: string | null;
  unit_amount: number;  // Price in cents (e.g., 7900 for $79)
  amount: number;       // Price in dollars (e.g., 79.0)
  currency: string;
  lookup_key: string;
  is_fallback?: boolean;  // True if using default prices (Stripe unavailable)
}

export interface ProductPricing {
  starter: StripePrice;
  growth: StripePrice;
}

export interface PricingResponse {
  comply: ProductPricing;
  quality: ProductPricing;
  full: ProductPricing;
  cache_initialized: boolean;
  prices_from_stripe: boolean;
}
